<template>
  <el-dialog
    title="Настройки прокси"
    :visible.sync="localShow"
    :width="isMobile ? '95%' : '520px'"
    top="8vh"
    @open="onOpen"
    @close="onClose"
    custom-class="proxy-dialog"
  >
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <i class="el-icon-loading" style="font-size: 32px; color: #5e72e4;"></i>
      <p class="mt-3 text-muted">Загрузка конфигурации...</p>
    </div>

    <!-- Main Form -->
    <div v-else>
      <!-- Error Alert -->
      <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
        <span class="alert-icon"><i class="ni ni-fat-remove"></i></span>
        <span class="alert-text">{{ errorMessage }}</span>
        <button type="button" class="close" @click="errorMessage = ''">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <!-- Toggle for using same proxy for both protocols -->
      <div class="form-group mb-4">
        <base-checkbox v-model="useSameForBoth">
          Использовать для HTTP и HTTPS
        </base-checkbox>
      </div>

      <!-- HTTP Proxy Section -->
      <div class="proxy-section">
        <h6><i class="ni ni-world mr-2"></i>HTTP Прокси</h6>

        <div class="row">
          <div class="col-md-8">
            <base-input
              label="Хост"
              placeholder="proxy.example.com"
              v-model="httpProxy.host"
              prepend-icon="ni ni-compass-04"
            />
          </div>
          <div class="col-md-4">
            <base-input
              label="Порт"
              type="number"
              placeholder="8080"
              v-model="httpProxy.port"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <base-input
              label="Имя пользователя"
              placeholder="Необязательно"
              v-model="httpProxy.username"
              prepend-icon="ni ni-single-02"
            />
          </div>
          <div class="col-md-6">
            <base-input
              label="Пароль"
              type="password"
              placeholder="Необязательно"
              v-model="httpProxy.password"
              prepend-icon="ni ni-lock-circle-open"
            />
          </div>
        </div>
      </div>

      <!-- HTTPS Proxy Section (only shown when useSameForBoth is false) -->
      <div v-if="!useSameForBoth" class="proxy-section">
        <h6><i class="ni ni-world mr-2"></i>HTTPS Прокси</h6>

        <div class="row">
          <div class="col-md-8">
            <base-input
              label="Хост"
              placeholder="proxy.example.com"
              v-model="httpsProxy.host"
              prepend-icon="ni ni-compass-04"
            />
          </div>
          <div class="col-md-4">
            <base-input
              label="Порт"
              type="number"
              placeholder="8080"
              v-model="httpsProxy.port"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <base-input
              label="Имя пользователя"
              placeholder="Необязательно"
              v-model="httpsProxy.username"
              prepend-icon="ni ni-single-02"
            />
          </div>
          <div class="col-md-6">
            <base-input
              label="Пароль"
              type="password"
              placeholder="Необязательно"
              v-model="httpsProxy.password"
              prepend-icon="ni ni-lock-circle-open"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Actions -->
    <span slot="footer" class="dialog-footer">
      <base-button type="secondary" @click="closeModal">
        Отмена
      </base-button>
      <base-button
        type="danger"
        @click="confirmDelete"
        v-if="hasExistingProxy && !loading"
      >
        Удалить
      </base-button>
      <base-button
        type="primary"
        @click="saveProxy"
        :disabled="saving || loading"
      >
        <i v-if="saving" class="el-icon-loading"></i>
        Сохранить
      </base-button>
    </span>
  </el-dialog>
</template>

<script>
import axios from 'axios';
import BaseInput from '@/components/Inputs/BaseInput.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseCheckbox from '@/components/Inputs/BaseCheckbox.vue';
import {
  parseProxyUrl,
  buildProxyUrl,
  validateProxy,
  isProxyEmpty,
  createEmptyProxy
} from '@/util/proxyUtils';

export default {
  name: 'ProxyConfigModal',

  components: {
    BaseInput,
    BaseButton,
    BaseCheckbox
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },
    providerId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      saving: false,
      useSameForBoth: true,
      hasExistingProxy: false,
      errorMessage: '',
      httpProxy: createEmptyProxy(),
      httpsProxy: createEmptyProxy()
    };
  },

  computed: {
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      }
    },
    isMobile() {
      return window.innerWidth < 576;
    }
  },

  methods: {
    /**
     * Called when modal opens
     */
    async onOpen() {
      this.errorMessage = '';
      await this.fetchProxyConfig();
    },

    /**
     * Fetch proxy configuration from API
     */
    async fetchProxyConfig() {
      this.loading = true;

      try {
        const response = await axios.get(`dashboard/providers/${this.providerId}/proxy`);

        if (response.data.status === true) {
          const { proxy_http, proxy_https } = response.data.data;

          // Parse HTTP proxy
          if (proxy_http) {
            const parsed = parseProxyUrl(proxy_http);
            if (parsed) {
              this.httpProxy = parsed;
              this.hasExistingProxy = true;
            }
          }

          // Parse HTTPS proxy
          if (proxy_https) {
            const parsed = parseProxyUrl(proxy_https);
            if (parsed) {
              this.httpsProxy = parsed;
              this.hasExistingProxy = true;
            }
          }

          // Check if both proxies are identical
          if (proxy_http && proxy_https && proxy_http === proxy_https) {
            this.useSameForBoth = true;
          } else if (proxy_http && proxy_https && proxy_http !== proxy_https) {
            this.useSameForBoth = false;
          }
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          await this.$router.push({ name: 'login' });
        } else if (error.response && error.response.status === 404) {
          // No proxy configured, show empty form
          this.hasExistingProxy = false;
        } else {
          this.errorMessage = 'Ошибка при загрузке конфигурации прокси';
          console.error('Error fetching proxy config:', error);
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * Save proxy configuration
     */
    async saveProxy() {
      this.errorMessage = '';

      // Determine which proxy to validate
      const proxyToValidate = this.useSameForBoth ? this.httpProxy : null;

      // Validate HTTP proxy
      const httpValidation = validateProxy(this.httpProxy);
      if (!httpValidation.valid) {
        this.errorMessage = httpValidation.errors.join(', ');
        return;
      }

      // Validate HTTPS proxy if not using same for both
      if (!this.useSameForBoth) {
        const httpsValidation = validateProxy(this.httpsProxy);
        if (!httpsValidation.valid) {
          this.errorMessage = 'HTTPS: ' + httpsValidation.errors.join(', ');
          return;
        }
      }

      // Build proxy URLs
      const httpUrl = buildProxyUrl(this.httpProxy);
      const httpsUrl = this.useSameForBoth
        ? buildProxyUrl(this.httpProxy)
        : buildProxyUrl(this.httpsProxy);

      // If both are empty, treat as delete
      if (!httpUrl && !httpsUrl) {
        if (this.hasExistingProxy) {
          await this.deleteProxy();
        } else {
          this.closeModal();
        }
        return;
      }

      this.saving = true;

      try {
        const response = await axios.put(`dashboard/providers/${this.providerId}/proxy`, {
          proxy_http: httpUrl || '',
          proxy_https: httpsUrl || ''
        });

        if (response.data.status === true) {
          this.$emit('success', response.data.message || 'Прокси настроен успешно');
          this.closeModal();
        } else {
          this.errorMessage = response.data.message || 'Ошибка при сохранении';
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          await this.$router.push({ name: 'login' });
        } else if (error.response && error.response.status === 422) {
          // Validation error from server
          const errors = error.response.data.errors;
          if (errors) {
            const errorMessages = Object.values(errors).flat();
            this.errorMessage = errorMessages.join(', ');
          } else {
            this.errorMessage = error.response.data.message || 'Ошибка валидации';
          }
        } else {
          this.errorMessage = 'Ошибка при сохранении конфигурации';
          console.error('Error saving proxy config:', error);
        }
        this.$emit('error', this.errorMessage);
      } finally {
        this.saving = false;
      }
    },

    /**
     * Confirm and delete proxy configuration
     */
    async confirmDelete() {
      const confirmed = window.confirm('Вы уверены, что хотите удалить конфигурацию прокси?');
      if (confirmed) {
        await this.deleteProxy();
      }
    },

    /**
     * Delete proxy configuration
     */
    async deleteProxy() {
      this.saving = true;

      try {
        const response = await axios.delete(`dashboard/providers/${this.providerId}/proxy`);

        if (response.data.status === true) {
          this.$emit('success', response.data.message || 'Конфигурация прокси удалена');
          this.closeModal();
        } else {
          this.errorMessage = response.data.message || 'Ошибка при удалении';
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          await this.$router.push({ name: 'login' });
        } else {
          this.errorMessage = 'Ошибка при удалении конфигурации';
          console.error('Error deleting proxy config:', error);
        }
        this.$emit('error', this.errorMessage);
      } finally {
        this.saving = false;
      }
    },

    /**
     * Close modal and reset form
     */
    closeModal() {
      this.localShow = false;
    },

    /**
     * Called when modal closes
     */
    onClose() {
      // Reset form to empty state
      this.httpProxy = createEmptyProxy();
      this.httpsProxy = createEmptyProxy();
      this.useSameForBoth = true;
      this.hasExistingProxy = false;
      this.errorMessage = '';
      this.loading = false;
      this.saving = false;
    }
  }
};
</script>

<style scoped>
.proxy-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 0.375rem;
}

.proxy-section h6 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #32325d;
  text-transform: uppercase;
  margin-bottom: 1rem;
  letter-spacing: 0.5px;
}

.loading-container {
  text-align: center;
  padding: 2rem;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.alert {
  margin-bottom: 1rem;
}

/* Mobile responsive styles */
@media (max-width: 576px) {
  .proxy-section {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }

  .proxy-section h6 {
    font-size: 0.8rem;
    margin-bottom: 0.75rem;
  }

  .dialog-footer {
    flex-direction: column-reverse;
    gap: 0.5rem;
  }

  .dialog-footer button {
    width: 100%;
    margin: 0 !important;
  }
}
</style>

<style>
/* Global styles for el-dialog on mobile */
@media (max-width: 576px) {
  .proxy-dialog {
    margin: 5vh auto !important;
  }

  .proxy-dialog .el-dialog__header {
    padding: 15px 15px 10px;
  }

  .proxy-dialog .el-dialog__body {
    padding: 10px 15px;
  }

  .proxy-dialog .el-dialog__footer {
    padding: 10px 15px 15px;
  }
}
</style>
